import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatPrice } from "@/lib/utils";
import type { Client } from "@/types/client";
import { Modal } from "antd";
import { AlertTriangle } from "lucide-react";

interface CreditOverLimitModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  selectedClient: Client | null;
  total: number;
}

export function CreditOverLimitModal({
  open,
  onCancel,
  onConfirm,
  selectedClient,
  total,
}: CreditOverLimitModalProps) {
  const limit = Number(selectedClient?.credit_limit) || 0;
  const balance = Number(selectedClient?.credit_balance) || 0;
  const projectedUsed = balance + total;
  const exceededAmount = projectedUsed - limit;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 text-amber-500 font-semibold text-base">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <span>Autorización de Sobregiro de Crédito</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <div className="flex items-center justify-end gap-4 pt-2">
          <Button key="change-method" variant="outline" onClick={onCancel}>
            Cambiar método de pago
          </Button>
          <Button
            key="allow-overlimit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={onConfirm}
          >
            Permitir sobrepasar cupo y cobrar
          </Button>
        </div>
      }
    >
      <div className="py-3 space-y-4">
        <p className="text-sm">
          El cliente{" "}
          <strong className="text-primary">{selectedClient?.name}</strong>{" "}
          superará el límite de crédito asignado para su cuenta.
        </p>

        <div className="bg-muted/40 rounded-lg p-3 space-y-2 text-sm border border-border">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Tope de crédito asignado:
            </span>
            <span className="font-semibold">{formatPrice(limit)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Crédito usado actualmente:
            </span>
            <span className="font-semibold text-amber-500">
              {formatPrice(balance)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Monto de la orden a cobrar:
            </span>
            <span className="font-semibold text-primary">
              +{formatPrice(total)}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-bold">
            <span>Crédito final proyectado:</span>
            <span className="text-red-500">{formatPrice(projectedUsed)}</span>
          </div>
          <div className="flex justify-between text-xs text-red-400">
            <span>Monto excedido:</span>
            <span>{formatPrice(exceededAmount)}</span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Como Administrador, puedes autorizar que el cliente sobrepase el total
          de su crédito o cancelar para seleccionar un método de pago distinto
          (Efectivo, Tarjeta, etc.).
        </p>
      </div>
    </Modal>
  );
}
